import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { User, AuthContextType, RegisterData, OnboardingData, ApiResponse } from '../types';
import { isPaymentRequiredError, redirectToPaidSignup, shouldLogoutOnAuthError } from '../lib/authErrors';
import { clearAuthenticatedResources } from '../lib/authenticatedResource';
import { clearEntryCode } from '../lib/signupEntry';
import { errorCode, userErrorMessage } from '../lib/userErrors';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const BACKEND_URL = (() => {
  const raw =
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_BACKEND_URL) ||
    process.env.REACT_APP_BACKEND_URL ||
    '';

  if (!raw) {
    if (process.env.NODE_ENV === 'production') {
      throw new Error('❌ REACT_APP_BACKEND_URL no está definida.');
    }
    console.warn('⚠️ Usando fallback localhost (solo desarrollo)');
    return 'http://localhost:8000';
  }

  // Normaliza a URL absoluta y sin trailing slash
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');
  }

  // Si la app está en HTTPS, forzamos HTTPS (salvo localhost)
  const isHttpsPage = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const isLocalhost = ['localhost', '127.0.0.1'].includes(url.hostname);

  if (isHttpsPage && url.protocol === 'http:' && !isLocalhost) {
    url.protocol = 'https:';
  }

  // devuelve solo el origen, sin path ni trailing slash
  return url.origin;
})();

const API = `${BACKEND_URL}/api`;

// ✅ CREAR CLIENTE API UNIFICADO
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Airbag: interceptor que convierte accidental http:// a https:// cuando la página corre sobre HTTPS
// ✅ INTERCEPTOR CENTRALIZADO: HTTPS + Authorization token
api.interceptors.request.use(
  (config) => {
    const isHttpsPage = typeof window !== 'undefined' && window.location.protocol === 'https:';

    if (isHttpsPage) {
      if (config.baseURL && typeof config.baseURL === 'string' && config.baseURL.startsWith('http://')) {
        config.baseURL = config.baseURL.replace(/^http:\/\//, 'https://');
      }
      if (typeof config.url === 'string' && config.url.startsWith('http://')) {
        config.url = config.url.replace(/^http:\/\//, 'https://');
      }
    }

    // ✅ CENTRALIZADO: Leer token de localStorage en cada request
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (err) => Promise.reject(err)
);

// ✅ INTERCEPTOR DE RESPUESTA: Sacar al usuario automáticamente si el token expiró (401)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (shouldLogoutOnAuthError(error)) {
      clearAuthenticatedResources();
      localStorage.removeItem('auth_token');
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login?expired=true';
      }
    } else if (isPaymentRequiredError(error)) {
      redirectToPaidSignup();
    }
    return Promise.reject(error);
  }
);

// ✅ Interceptor para axios global (compatibilidad con código legacy)
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (err) => Promise.reject(err)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (shouldLogoutOnAuthError(error)) {
      clearAuthenticatedResources();
      localStorage.removeItem('auth_token');
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login?expired=true';
      }
    } else if (isPaymentRequiredError(error)) {
      redirectToPaidSignup();
    }
    return Promise.reject(error);
  }
);

// ✅ MANTENER axios.defaults.baseURL para compatibilidad (pero prefiere usar api)
axios.defaults.baseURL = API;
console.log('🎯 [AuthContext] axios.defaults.baseURL SET TO:', axios.defaults.baseURL);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));

  // Load user on mount if token exists
  useEffect(() => {
    const loadUser = async (): Promise<void> => {
      if (token) {
        try {
          const response = await axios.get('/auth/me');
          setUser(response.data);
        } catch (error: any) {
          console.error('Failed to load user:', error);
          // ✅ SOLO borrar el token si el servidor dice explícitamente que es inválido (401/403)
          // Si es un error 500, 502 (reinicios) o de red, mantenemos el token para que el usuario no pierda la sesión
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            clearAuthenticatedResources();
            localStorage.removeItem('auth_token');
            setToken(null);
          }
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Send OTP code to email
  const sendOTP = async (email: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axios.post('/auth/send-otp', { email });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: userErrorMessage(error, 'No pudimos enviar el código. Intenta de nuevo.'),
        code: errorCode(error)
      };
    }
  };

  // Verify OTP and login
  const verifyOTP = async (email: string, otpCode: string): Promise<ApiResponse<{ user: User }>> => {
    try {
      const response = await axios.post('/auth/verify-otp', {
        email,
        otp_code: otpCode
      });

      const { access_token, user: userData } = response.data;
      
      // Store token (el interceptor lo leerá automáticamente en cada request)
      localStorage.setItem('auth_token', access_token);
      // La cuenta ya existe: su flujo (paywall o no) vive en la base de datos.
      clearEntryCode();
      
      setToken(access_token);
      setUser(userData);

      return { success: true, data: { user: userData } };
    } catch (error: any) {
      return {
        success: false,
        error: userErrorMessage(error, 'Código incorrecto.'),
        code: errorCode(error)
      };
    }
  };

  // Register user
  const register = async (userData: RegisterData): Promise<ApiResponse<User>> => {
    try {
      // `api` sends credentials so the rv_ft / rv_lt attribution cookies reach the backend.
      const response = await api.post('/auth/register', userData);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  // Helper function to extract error message from API response
  const extractErrorMessage = (error: any): string => {
    const responseData = error.response?.data;
    
    // Handle standard FastAPI HTTPException detail
    if (typeof responseData?.detail === 'string') {
      return responseData.detail;
    }
    
    // Handle Pydantic validation errors (array of errors)
    if (Array.isArray(responseData?.detail)) {
      const validationErrors = responseData.detail;
      // Get the first error message for display
      if (validationErrors.length > 0) {
        const firstError = validationErrors[0];
        const field = firstError.loc?.join('.') || '';
        const msg = firstError.msg || 'Validation error';
        return field ? `${field}: ${msg}` : msg;
      }
    }
    
    // Handle error object with message
    if (responseData?.message) {
      return responseData.message;
    }
    
    // Handle error object with error field
    if (responseData?.error) {
      return typeof responseData.error === 'string' 
        ? responseData.error 
        : JSON.stringify(responseData.error);
    }
    
    return 'Unknown error occurred';
  };

  // Complete onboarding
  const completeOnboarding = async (onboardingData: OnboardingData): Promise<ApiResponse<any>> => {
    try {
      const response = await axios.post('/auth/onboarding', onboardingData);
      
      // Update user with company data
      setUser(response.data.user);
      
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: extractErrorMessage(error)
      };
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    try {
      await axios.post('/auth/logout');
    } finally {
      clearAuthenticatedResources();
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    }
  };

  // Re-fetch the current user (e.g. after a payment lifts payment_required)
  const refreshUser = useCallback(async (): Promise<User | null> => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
      return response.data as User;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  }, []);

  const startSession = useCallback((accessToken: string, sessionUser: User): void => {
    clearAuthenticatedResources();
    localStorage.setItem('auth_token', accessToken);
    setToken(accessToken);
    setUser(sessionUser);
  }, []);

  const needsOnboarding = (): boolean => false;

  // Check if user is admin
  const isAdmin = (): boolean => {
    return user !== null && user.role === 'admin';
  };

  const value: AuthContextType = {
    user,
    loading,
    token,
    sendOTP,
    verifyOTP,
    register,
    completeOnboarding,
    logout,
    refreshUser,
    startSession,
    needsOnboarding,
    isAdmin,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;